// Home's "Vrienden proefden": the latest tastings friends and group members chose to share, a few
// rows with the line they added (only when they added one) and the reactions cards already have. A
// share is the author's explicit act, so there is nothing here that was not offered to this person.
// Only an account has friends, so a signed-out visitor, or nothing shared, or a list that cannot be
// read, shows nothing at all instead of an empty or broken block on home.
import type { TastingShareView } from "@proef/contract";
import { useEffect, useState } from "react";
import { useT } from "../lib/i18n";
import { navigate } from "../lib/router";
import { useSession } from "../lib/social/auth";
import { listTastingShares } from "../lib/social/tastingSharesApi";
import { useShareReactions } from "../lib/social/useShareReactions";
import { Column, Section, TextAction } from "../ui";
import { PersonSheet } from "./PersonSheet";
import { SharedTastingCard } from "./SharedTastingCard";

/** How many shares home shows; the rest are one tap away in Samen. */
const HOME_ROWS = 5;

export function FriendsFeed(): React.JSX.Element | null {
  const { t } = useT();
  const auth = useSession();
  const signedIn = auth.status === "signedIn";
  const [shares, setShares] = useState<readonly TastingShareView[]>([]);
  const [person, setPerson] = useState<string | undefined>(undefined);
  const { reacting, errors, react } = useShareReactions((updated) => {
    setShares((current) => current.map((share) => (share.id === updated.id ? updated : share)));
  });

  useEffect(() => {
    if (!signedIn) {
      setShares([]);
      return undefined;
    }
    let cancelled = false;
    listTastingShares({ limit: HOME_ROWS })
      .then((page) => {
        if (!cancelled) setShares(page.items);
        return undefined;
      })
      // The block is a glance: when the list cannot be read the rest of home is still right.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  if (shares.length === 0) return null;
  const hint = shares.find((share) => share.user.id === person)?.user;

  return (
    <Section title={t("social.shared.title")} data-testid="friends-feed">
      <Column gap="3">
        {shares.map((share) => (
          <SharedTastingCard
            key={share.id}
            testId={`friends-feed-${share.id}`}
            share={share}
            reacting={reacting.has(share.id)}
            error={errors.get(share.id)}
            onReact={(kind) => {
              react(share.id, kind);
            }}
            onOpenPerson={setPerson}
          />
        ))}
        <TextAction
          label={t("social.shared.all")}
          onPress={() => {
            navigate("/samen/activity");
          }}
        />
      </Column>
      {person !== undefined && (
        <PersonSheet
          userId={person}
          hint={hint}
          onClose={() => {
            setPerson(undefined);
          }}
        />
      )}
    </Section>
  );
}
